import { CorporationDataType } from '@commenda/types/src/corporation';
import {
  DirectorBase,
  TValidateStakeholdersResponse,
} from '@commenda/types/src/incorporation/types';
import { getCorporationName } from '@commenda/utils';
import { LegacyDocumentType, StakeholderType } from '@prisma/client';
import { ZodError } from 'zod';
import { corporationDetailsSchema } from '../common/corporateStakeholder/CorporationDetailsForm';
import { getDirectorName } from '../Director/common';
import { TShareholderVsSharePercentageMap } from '../EquityInfo/types';
import { directorSchema, responsiblePartyInfoSchema } from './schema';

const validateDirectorInfo = (
  directors: DirectorBase[],
  selectedDirectorIds: number[]
) => {
  const directorSchemaWithoutPassport = directorSchema.omit({
    passportProof: true,
  });
  const directorWithError = [];
  const selectedDirectors = directors?.filter((director) =>
    selectedDirectorIds.includes(director.id)
  );

  for (let i = 0; i < selectedDirectors.length; i += 1) {
    const passportProof = selectedDirectors[i].directorFiles?.find(
      (file) => file.documentType === LegacyDocumentType.Passport
    );

    if (!passportProof) {
      directorWithError.push({
        name: getDirectorName(selectedDirectors[i]),
        id: selectedDirectors[i]?.id,
      });
    }

    const validationResult = directorSchemaWithoutPassport.safeParse({
      ...selectedDirectors[i],
      passportProof,
    }) as {
      success: boolean;
      error: ZodError;
    };

    if (!validationResult.success) {
      if (validationResult.error) {
        directorWithError.push({
          name: getDirectorName(selectedDirectors[i]),
          id: selectedDirectors[i]?.id,
        });
      }
    }
  }

  return directorWithError;
};

const validateUSStakeholders = (
  selectedStakeholders: TShareholderVsSharePercentageMap,
  {
    directors,
    corporations,
  }: { directors: DirectorBase[]; corporations: CorporationDataType[] }
): TValidateStakeholdersResponse[] => {
  const shareholderWithError: TValidateStakeholdersResponse[] = [];

  Object.keys(selectedStakeholders).forEach((stakeholderId) => {
    const [type, id] = stakeholderId.split('_');

    if (type === StakeholderType.INDIVIDUAL) {
      const director = directors.find((d) => d.id === +id);

      if (!director) return;

      const invalidDirectors = validateDirectorInfo(directors, [+id]);

      if (invalidDirectors.length > 0) {
        shareholderWithError.push({
          name: getDirectorName(director),
          id: stakeholderId,
        });
      }
    }

    if (type === StakeholderType.INSTITUTION) {
      const corporation = corporations.find((c) => c.id === +id);

      if (!corporation) return;

      const corporationBasicInformationValidationResult =
        corporationDetailsSchema.safeParse(corporation) as {
          success: boolean;
          error: ZodError;
        };

      if (!corporationBasicInformationValidationResult.success) {
        if (corporationBasicInformationValidationResult.error) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      }

      const authorizedSignatory = corporation.corporationHasDirector?.find(
        (director) =>
          director.additionalDirectorInformation?.isAuthorizedSignatory
      );

      if (authorizedSignatory) {
        const director = directors.find(
          (d) => d.id === authorizedSignatory.directorId
        );

        const directorValidationResult = validateDirectorInfo(directors, [
          authorizedSignatory.directorId,
        ]);

        if (directorValidationResult.length > 0) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      } else {
        shareholderWithError.push({
          name: getCorporationName(corporation),
          id: stakeholderId,
        });
      }
    }
  });

  return shareholderWithError;
};

const validateResponsiblePartyInfo = (
  directors: {
    id: number;
    directorFirstName: string;
    directorMiddleName?: string;
    directorLastName: string;
    SSINorITIN: string;
    hasSSINorITIN: boolean;
  }[],
  selectedDirectorIds: number[]
) => {
  const directorWithError = [];
  const selectedDirectors = directors?.filter((director) =>
    selectedDirectorIds.includes(director.id)
  );

  const dirsToParse = selectedDirectors.map((director) => ({
    ...director,
    hasSSINorITIN: director.hasSSINorITIN ? true : false,
    SSINorITIN: director.SSINorITIN?.padEnd(9, '0') || '', // If we don't pad it, the schema will fail because the ssnMask is only 4 digits
  }));

  for (let i = 0; i < dirsToParse.length; i += 1) {
    const validationResult = responsiblePartyInfoSchema.safeParse(
      dirsToParse[i]
    ) as {
      success: boolean;
      error: ZodError;
    };

    if (!validationResult.success) {
      if (validationResult.error) {
        const director = {
          directorFirstName: selectedDirectors[i].directorFirstName,
          directorMiddleName: selectedDirectors[i].directorMiddleName,
          directorLastName: selectedDirectors[i].directorLastName,
          number: '', // getDirectorName doesn't need number but changing arg type will break other code
          email: '', // getDirectorName doesn't need email but changing arg type will break other code
        };
        directorWithError.push({
          name: getDirectorName(director),
          id: selectedDirectors[i]?.id,
          error: validationResult.error,
        });
      }
    }
  }

  console.log('directorWithError', directorWithError);

  return directorWithError;
};

export {
  validateDirectorInfo,
  validateResponsiblePartyInfo,
  validateUSStakeholders,
};
